/* Push notification helpers */
import api from "@/services/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
  return output;
}

export async function setupPushNotifications() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  try {
    // iOS/Android REQUIRE Notification.requestPermission() to run synchronously
    // inside the user gesture, BEFORE any other await (e.g. SW registration).
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return false;
    const reg = await navigator.serviceWorker.register("/sw.js");
    const { public_key } = await api.getVapidPublicKey();
    if (!public_key) return false;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
    const json = sub.toJSON();
    await api.pushSubscribe({ endpoint: json.endpoint, keys: json.keys });
    return true;
  } catch (err) {
    console.error("setupPushNotifications failed", err);
    return false;
  }
}

// Subscribe assuming permission is ALREADY granted. Uses the already-registered
// SW via navigator.serviceWorker.ready (no re-register) to avoid the micro-delay
// that makes mobile browsers drop the permission prompt. Returns true on success.
export async function subscribeReadySW() {
  try {
    console.log("[push] 1. subscribeReadySW start, Notification.permission =", typeof Notification !== "undefined" ? Notification.permission : "n/a");

    // Wait until the Service Worker is FULLY active before generating the token.
    const reg = await navigator.serviceWorker.ready;
    console.log("[push] 2. serviceWorker.ready OK, scope =", reg.scope, "active =", !!reg.active);

    const { public_key } = await api.getVapidPublicKey();
    console.log("[push] 3. VAPID public key from backend =", public_key ? public_key.slice(0, 16) + "..." : "(empty!)");
    if (!public_key) {
      console.error("[push] ABORT: backend returned no VAPID public key");
      return false;
    }

    // Reuse / reset any existing subscription. After a VAPID key rotation an old
    // subscription is bound to the OLD key → subscribe() throws InvalidStateError,
    // so we must unsubscribe it first.
    const existing = await reg.pushManager.getSubscription();
    console.log("[push] 4. getSubscription() existing =", existing ? existing.endpoint : "none");
    if (existing) {
      const ok = await existing.unsubscribe();
      console.log("[push] 4b. unsubscribed old subscription =", ok);
    }

    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
    const json = sub.toJSON();
    console.log("[push] 5. pushManager.subscribe() OK, endpoint =", json.endpoint);
    console.log("TOKEN_NOTIF_GENERE:", json);

    const res = await api.pushSubscribe({ endpoint: json.endpoint, keys: json.keys });
    console.log("[push] 6. POST /api/notifications/subscribe OK, response =", res);
    return true;
  } catch (err) {
    console.error("[push] FAILED at some step:", err && err.name, err && err.message, err);
    return false;
  }
}

// Unsubscribe the browser push subscription (switch turned OFF).
export async function unsubscribeReadySW() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
    return true;
  } catch (err) {
    console.error("unsubscribeReadySW failed", err);
    return false;
  }
}

// Silent re-subscribe when permission is ALREADY granted (no prompt).
// Safe to call on every app load to keep the subscription fresh across devices.
export async function resubscribeIfGranted() {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    const { public_key } = await api.getVapidPublicKey();
    if (!public_key) return false;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
    const json = sub.toJSON();
    await api.pushSubscribe({ endpoint: json.endpoint, keys: json.keys });
    return true;
  } catch (_) {
    return false;
  }
}

// True when the browser has already granted notification permission.
export function notificationsActive() {
  try {
    return typeof Notification !== "undefined" && Notification.permission === "granted";
  } catch (_) {
    return false;
  }
}

// iOS only delivers Web Push when the app is installed to the home screen
// (standalone PWA). In a Safari tab, push is unavailable.
export function isIosNonStandalone() {
  const ua = navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua);
  const standalone =
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true;
  return iOS && !standalone;
}

// Request notification permission FIRST (native popup on user gesture), then
// subscribe. Returns true if granted. Opens the gate modal if blocked.
export async function ensurePushPermission() {
  if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) {
    window.dispatchEvent(new Event("matchup:push-gate"));
    return false;
  }
  if (Notification.permission === "denied") {
    window.dispatchEvent(new Event("matchup:push-gate"));
    return false;
  }
  const ok = await setupPushNotifications(); // calls requestPermission() then subscribes
  if (!ok) window.dispatchEvent(new Event("matchup:push-gate"));
  return ok;
}

// Gate helper: returns true if notifications are active; otherwise opens the
// global push modal (via window event) and returns false so the caller can
// abort the blocked action (create/join).
export function ensureNotifications() {
  if (notificationsActive()) return true;
  window.dispatchEvent(new Event("matchup:push-gate"));
  return false;
}
